import { Search, Menu } from 'lucide-react' // Filter, ChevronDown, Grid, List, Plus
import { useLocation } from 'react-router-dom'
import ProfileMenu from './ProfileMenu'
import useExploreStore from '@/store/useExploreStore'
import { cn } from '@/lib/utils'
import AddFriendButton from '@/components/friends/AddFriendButton'
// import useProjectStore from '@/store/useProjectStore'
// import Button from '@/components/ui/Button'
import Dropdown from '@/components/ui/Dropdown'
import useUIStore from '@/store/useUIStore'
import ColorWheelIcon from '@/components/ui/ColorWheelIcon'

interface TopNavProps {
  selectedCategory: string
  setSelectedCategory: (category: string) => void
  currentPageConfig: {
    title: string
    searchPlaceholder: string
    categories: string[]
    showTypes: boolean
    showFilters: boolean
  },
  setMobileSidebarOpen: (open: boolean) => void;
  className?: string;
}

export default function TopNav({
  selectedCategory,
  setSelectedCategory,
  currentPageConfig,
  setMobileSidebarOpen,
  className
}: TopNavProps) {
  const location = useLocation()
  // const uiSearchQuery = useUIStore(state => state.searchQuery);
  // const setUISearchQuery = useUIStore(state => state.setSearchQuery);
  const uiSetIsTypesOpen = useUIStore(state => state.setIsTypesOpen);
  const {
    contentType,
    setContentType,
    searchQuery: exploreSearchQuery,
    setSearchQuery: setExploreSearchQuery,
    // sortBy: exploreSortBy,
    // setSortBy: setExploreSortBy,
    // viewMode: exploreViewMode,
    // setViewMode: setExploreViewMode
  } = useExploreStore()
  // Removed project sort functionality per user request
  // const { sortBy: projectSortBy, setSortBy: setProjectSortBy } = useProjectStore()

  // const projectSortOptions = [
  //   { value: 'recent', label: 'Recently Added' },
  //   { value: 'popular', label: 'Most Popular' },
  //   { value: 'alphabetical', label: 'Alphabetical' }
  // ]

  const isWorkspacePage = location.pathname.startsWith('/workspace/')

  return (
    <div className={cn(
      "sticky top-0 right-0 z-40 navbar",
      className
    )}>
      <div className="flex h-16 items-center justify-between gap-x-2 px-2 sm:gap-x-4 sm:px-4 lg:gap-x-6 lg:px-8">
        {/* --- Wrapper for Left + Middle Sections --- */}
        <div className="flex flex-1 items-center gap-x-2 sm:gap-x-4 min-w-0">
          {/* --- Left Section (Mobile Menu Button, Search, General Filters) --- */}
          <div className="flex items-center gap-x-2 sm:gap-x-4 shrink-0">
            {/* Mobile Menu Button */}
            <button
              type="button"
              className="lg:hidden p-2.5 text-zinc-400 hover:text-white"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Global Search - Only show on explore page */}
            {location.pathname === '/explore' && (
              <div className="relative max-w-lg flex-1">
                <Search className="absolute left-2 sm:left-3 top-1/2 h-4 sm:h-4 w-4 sm:w-4 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder={currentPageConfig.searchPlaceholder}
                  value={exploreSearchQuery}
                  onChange={(e) => setExploreSearchQuery(e.target.value)}
                  className="input-search w-full h-9 text-sm"
                />
              </div>
            )}

            {/* Color Wheel */}
            <ColorWheelIcon />


            {/* General Types Dropdown - Hide on Explore page, Projects page, and Single Project pages */}
            {!isWorkspacePage && currentPageConfig.showTypes && currentPageConfig.categories.length > 0 && location.pathname !== '/explore' && location.pathname !== '/projects' && !location.pathname.startsWith('/projects/') && (
              <Dropdown
                value={selectedCategory}
                onChange={(value) => {
                  setSelectedCategory(value);
                  uiSetIsTypesOpen(false); // Ensure dropdown closes on selection
                }}
                options={currentPageConfig.categories.map(category => ({ value: category, label: category }))}
                className="relative hidden sm:block"
              />
            )}
          </div>

          {/* --- Middle Section (Dynamic Page Controls) --- */}
          <div className="hidden sm:flex items-center gap-x-2 sm:gap-x-4">
            {/* Explore Page Controls */}
            {location.pathname === '/explore' && (
              <>
                {/* Projects/Posts Toggle with Dropdown Styling */}
                <div className="relative">
                  <div className="flex items-center bg-container-800 backdrop-blur-sm border border-zinc-700/[var(--container-transparency)] rounded-lg overflow-hidden" data-liquid-reflect>
                    <button
                      onClick={() => setContentType('projects')}
                      className={cn(
                        'px-4 py-2.5 text-sm font-medium border-r border-zinc-700/[var(--container-transparency)]',
                        contentType === 'projects'
                          ? 'bg-[rgb(var(--accent-1))]/20 text-zinc-200'
                          : 'text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700/[calc(var(--container-transparency)*0.5)]'
                      )}
                    >
                      Projects
                    </button>
                    <button
                      onClick={() => setContentType('posts')}
                      className={cn(
                        'px-4 py-2.5 text-sm font-medium',
                        contentType === 'posts'
                          ? 'bg-[rgb(var(--accent-1))]/20 text-zinc-200'
                          : 'text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700/[calc(var(--container-transparency)*0.5)]'
                      )}
                    >
                      Posts
                    </button>
                  </div>
                </div>

              </>
            )}

            {/* Projects Page Controls - Removed per user request */}
            {/* {location.pathname === '/projects' && (
              <>
                <Dropdown
                  value={projectSortBy}
                  onChange={(value) => setProjectSortBy(value as typeof projectSortBy)}
                  options={projectSortOptions}
                  className="flex-1 md:w-48"
                />
              </>
            )} */}

            {/* Friends Page Controls */}
            {location.pathname === '/friends' && (
              <AddFriendButton />
            )}

          </div>
        </div>

        {/* --- Right Section (Profile Menu) --- */}
        <div className="flex items-center shrink-0">
          <ProfileMenu />
        </div>
      </div>
    </div>
  )
} 